import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';
import T from 'prop-types';
import { get } from 'lodash-es';

import Skeleton from '../../../../Skeleton';
import Button from '../../../../Buttons/Button';
import ErrorStateWithReload from '../../../../ErrorStateWithReload';
import InsightsCardBox from '../../../../../pages/app-insights/_shared/components/InsightsCardBox';
import YearChart from '../../../../../pages/app-insights/_shared/components/YearChart';
import QuarterChart from '../../../../../pages/app-insights/_shared/components/QuarterChart';

import { permissionsSelector } from '../../../selectors';
import links from '../../../../../pages/app-insights/_shared/config/links';
import { getMetricLabel } from '../../../../../pages/app-insights/_shared/config/finance/metrics';

const widgetKeys = ['growth', 'engagement'];

const activeMetric = {
  growth: 'downloads',
  engagement: 'dau',
};

const widgetTitles = {
  growth: 'New user growth',
  engagement: 'Engagement',
};

const SplitView = ({
  widgets,
  settings,
  maxDate,
}) => {
  const { filters } = widgets;

  return (
    <div className="finance-granularity-chart-widgets split-view">
      {widgetKeys.map(widget => (
        <InsightsCardBox
          key={widget}
          bodyMinHeight={619}
          className="app-overview-performance"
          headerLeft={(
            <h3 className="h3">
              {widgetTitles[widget]}
              {widget === 'engagement' && <span className="text-capture">(daily active users)</span>}
              {widget === 'growth' && <span className="text-capture">(downloads)</span>}
            </h3>
          )}
          headerRight={filters && (
            <div className="dropdowns-section">
              <Button
                variant="secondary"
                href={links[widget](filters.store, filters.ref_no)}
              >
                View Performance
                <i className="hicon-angle-right" />
              </Button>
            </div>
          )}
        >
          {!settings.pending && settings.error && <ErrorStateWithReload /> }
          {!settings.error && (
            <div className="chart-wrapper">
              <div className="year-chart">
                <div className="filters-section">
                  Year-over-Year
                </div>
                {settings.pending && <Skeleton height={276} width="100%" />}
                {!settings.pending && (
                  <YearChart
                    series={get(widgets, ['performance', 'year_over_year'])}
                    metric={activeMetric[widget]}
                    granularity="weekly"
                    overPeriod="year-over-year"
                    rotateLabels
                    variant="qv"
                    maxDate={maxDate}
                    subDates={get(widgets, ['performance', 'year_sub_dates'])}
                  />
                )}
              </div>
              <div className="quarter-chart">
                <div className="filters-section">
                  Quarter-to-Date
                </div>
                {settings.pending && <Skeleton height={277} width="100%" />}
                {!settings.pending && (
                  <QuarterChart
                    series={get(widgets, ['performance', 'quarter_to_date'])}
                    metric={activeMetric[widget]}
                    variant="qv"
                  />
                )}
              </div>
            </div>
          )}
        </InsightsCardBox>
      ))}
    </div>
  );
};

SplitView.propTypes = {
  widgets: T.object.isRequired,
  settings: T.object.isRequired,
  maxDate: T.string,
};

const CombineView = ({
  widgets,
  settings,
  maxDate,
}) => {
  const decoratedwidgetTitles = {
    ...widgetTitles,
    growth: 'Growth',
  };

  return (
    <div className="finance-granularity-chart-widgets combine-view">
      <InsightsCardBox
        bodyMinHeight={619}
        className="app-overview-performance"
        headerLeft={(
          <h4 className="h4">
            All Users
          </h4>
        )}
      >
        {!settings.pending && settings.error && <ErrorStateWithReload /> }
        {!settings.error && (
          <div className="chart-wrapper">
            {widgetKeys.map((widget, index) => (
              <Fragment key={widget}>
                <div className="year-chart">
                  <div className="filters-section">
                    <div className="chart-caption">
                      {decoratedwidgetTitles[widget]} — {getMetricLabel(activeMetric[widget], widget)}
                      <span className="chart-caption__append">YoY</span>
                    </div>
                  </div>
                  {settings.pending && <Skeleton height={276} width="100%" />}
                  {!settings.pending && (
                    <YearChart
                      series={get(widgets, ['performance', 'year_over_year'])}
                      metric={activeMetric[widget]}
                      granularity="weekly"
                      overPeriod="year-over-year"
                      rotateLabels
                      variant="qv"
                      maxDate={maxDate}
                      subDates={get(widgets, ['performance', 'year_sub_dates'])}
                    />
                  )}
                </div>
                {(index !== widgetKeys.length - 1) && <div className="divider b-b" />}
              </Fragment>
            ))}
          </div>
        )}
      </InsightsCardBox>
    </div>
  );
};

CombineView.propTypes = {
  widgets: T.object.isRequired,
  settings: T.object.isRequired,
  maxDate: T.string,
};

const FinanceGranularityChartWidgets = ({ widgets, settings }) => {
  const permissions = useSelector(permissionsSelector);

  if (permissions.allowFinanceAppOverviewV3) {
    return (
      <CombineView
        widgets={widgets}
        settings={settings}
        maxDate={permissions.maxDate}
      />
    );
  }

  return (
    <SplitView
      widgets={widgets}
      settings={settings}
      maxDate={permissions.maxDate}
    />
  );
};

FinanceGranularityChartWidgets.propTypes = {
  widgets: T.object.isRequired,
  settings: T.object.isRequired,
};

export default FinanceGranularityChartWidgets;
